import { cn } from "@tine/designsystem-utils";
import isFunction from "lodash-es/isFunction";
import { action, makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "wouter";

import useContainerWidth, { type WidthRanges } from "../../../common/hooks/useContainerWidth";
import { productImageUrl } from "../../../common/imageUtils";
import { queueImpression, sendProductClick } from "../../../common/tracking";
import { PageType, type Product, type ProductTileCallbacks } from "../../../common/types/productTypes";
import type { GAListInformation } from "../../../common/types/trackingTypes";

import authStore from "../../../stores/auth/authStore";
import featuresStore from "../../../stores/features/featuresStore";
import productStore from "../../../stores/product/productStore";
import promotionStore from "../../../stores/promotionStore";
import uiStore from "../../../stores/uiStore";
import Badges from "../../Badges";
import Button from "../../Button";
import Favorite from "../../Favorite";
import Labels from "../../Labels";
import PriceBomb from "../PriceBomb";
import ProductTileLoggedIn from "./ProductTileLoggedIn";

type ProductTileProps = {
   product: Product;
   listInformation: GAListInformation;
   position: number;
   className?: string;
   callbacks?: ProductTileCallbacks;
};

export type InternalProductTileStore = {
   qty: number;
   hovering: boolean;
   hasReportedVisibility: boolean;
};

export type ProductTileSize = "thin" | "normal";
const ranges: WidthRanges<ProductTileSize> = {
   thin: [0, 190],
   normal: [190]
};
const ProductTile = ({ product, listInformation, position, className = "", callbacks = {} }: ProductTileProps) => {
   const imageRef = useRef<HTMLImageElement>(null);
   const tileRef = useRef<HTMLDivElement>(null);
   const { ref: containerRef, size: currentContainerSize } = useContainerWidth<ProductTileSize, HTMLDivElement>(ranges);
   const tileState: InternalProductTileStore = useMemo(
      () =>
         makeAutoObservable({
            qty: 1,
            hovering: false,
            hasReportedVisibility: false
         } satisfies InternalProductTileStore),
      []
   );

   const trackImpression = action((isVisible: boolean) => {
      if (isVisible && !tileState.hasReportedVisibility) {
         queueImpression(product, listInformation, position);
         // Allow other consumers to insert their own callback
         if (isFunction(callbacks.impression)) {
            callbacks.impression(product, listInformation.name, position);
         }
         tileState.hasReportedVisibility = true;
      }
   });

   const { ref } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   const checkIfWarn = action(() => {
      tileState.hovering = !authStore.isLoggedIn();
   });

   const removeWarning = action(() => {
      tileState.hovering = false;
   });

   const trackClick = () => {
      sendProductClick(product, listInformation, position);
      // Allow other consumers to insert their own callback
      if (isFunction(callbacks.click)) {
         callbacks.click(product, listInformation.name, position);
      }
   };

   const outerClassName = cn(
      "h-full rounded-lg shadow-sm hover:shadow-md",
      { "min-h-[340px]": featuresStore.productNavigationAvailable },
      className
   );

   return (
      <div ref={ref} className={outerClassName} data-testid={`product-tile-${product.sku}`}>
         <div ref={containerRef} className="h-full">
            {/** biome-ignore lint/a11y/noStaticElementInteractions: maybe this should be a link? */}
            <div
               ref={tileRef}
               className={cn("group relative flex min-h-full flex-col justify-between rounded-lg bg-base-0 p-3", {
                  "z-10 shadow-xl": tileState.hovering
               })}
               onMouseEnter={checkIfWarn}
               onMouseLeave={removeWarning}
            >
               <div className="flex flex-col">
                  <div className="relative">
                     <div className="absolute right-0 flex flex-col">
                        {!product.expired && <Favorite sku={product.sku} listInformation={listInformation} position={position} />}
                        <Badges badges={product.badges} page={PageType.ProductListPage} />
                     </div>

                     <Labels labels={product.labels} page={PageType.ProductListPage} />

                     <Link tabIndex={-1} to={productStore.getUrl(product)} onClick={trackClick} data-testid="product-link">
                        <img
                           alt={`Bilde av ${product.name}`}
                           ref={imageRef}
                           loading="lazy"
                           className="mx-auto my-4 block aspect-square w-7/12 p-1 group-hover:p-0 group-hover:transition-[padding] group-hover:duration-150"
                           src={productImageUrl(product.sku, 130)}
                        />
                        {promotionStore.getPromotionsBySku(product.sku).map((promotion) => (
                           <PriceBomb size="small" key={promotion.banner}>
                              {promotion.banner}
                           </PriceBomb>
                        ))}
                     </Link>
                  </div>
                  <div className="mb-1 text-sm/[16px] font-medium">
                     <Link to={productStore.getUrl(product)} onClick={trackClick}>
                        {product.name}
                     </Link>
                  </div>
                  <div className="mb-3 text-xxs text-base-600">Art.nr. {product.sku}</div>
               </div>
               <div className="flex flex-col justify-end">
                  <ProductTileLoggedIn
                     product={product}
                     tileState={tileState}
                     currentContainerSize={currentContainerSize}
                     callbacks={callbacks}
                     imageRef={imageRef}
                     position={position}
                     listInformation={listInformation}
                  />

                  {!authStore.isLoggedIn() && featuresStore.productNavigationAvailable && (
                     <Button className="mt-4" variant="tertiary" size="compact" onClick={uiStore.showLoginModal}>
                        Logg inn for kjøp
                     </Button>
                  )}
               </div>
            </div>
         </div>
      </div>
   );
};

export default observer(ProductTile);
